import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { DriverGroupDto, OrganizationUserDto } from '@cargovibe/shared-types';
import { Button } from '@/shared/ui/button';
import { SortHeader } from '@/shared/ui/SortHeader';
import { RoleBadge } from '@/modules/users/components/RoleBadge';
import { UserStatusBadge } from '@/modules/users/components/UserStatusBadge';
import { BookingUsage } from '@/modules/users/components/BookingUsage';
import { DriverGroupBadges } from '@/modules/driver-groups';
import { formatFullName } from '@/shared/services/formatFullName';

export type UserSortKey = keyof Pick<OrganizationUserDto, 'lastname' | 'status'>;
export type SortDirection = 'asc' | 'desc';

interface Props {
  users: OrganizationUserDto[];
  groupsByUserId: Map<string, DriverGroupDto[]>;
  sortKey: UserSortKey;
  sortDirection: SortDirection;
  onSort: (key: UserSortKey) => void;
  isSelectingMembers: boolean;
  selectedMemberIds: Set<string>;
  onToggleMember: (userId: string) => void;
  onEditUser: (user: OrganizationUserDto) => void;
  onDeleteUser: (user: OrganizationUserDto) => void;
}

export function UsersTable({
  users,
  groupsByUserId,
  sortKey,
  sortDirection,
  onSort,
  isSelectingMembers,
  selectedMemberIds,
  onToggleMember,
  onEditUser,
  onDeleteUser,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();

  const handleToggleMember = (userId: string) => (): void => {
    onToggleMember(userId);
  };

  const handleEditUser = (user: OrganizationUserDto) => (): void => {
    onEditUser(user);
  };

  const handleDeleteUser = (user: OrganizationUserDto) => (): void => {
    onDeleteUser(user);
  };

  return (
    <table className="w-full text-sm text-left">
      <thead className="text-xs text-muted-foreground bg-muted/50 border-b border-border uppercase">
        <tr>
          {isSelectingMembers && <th className="pl-6 pr-1 py-3 w-10" />}
          <SortHeader<UserSortKey>
            label={t('users.name')}
            sortKey="lastname"
            active={sortKey}
            direction={sortDirection}
            onSort={onSort}
          />
          <th className="px-6 py-3">{t('users.email')}</th>
          <th className="px-6 py-3">{t('users.mobileNumber')}</th>
          <th className="px-6 py-3">{t('users.role')}</th>
          <th className="px-6 py-3">{t('driverGroups.colGroups')}</th>
          <th className="px-6 py-3">{t('users.freeBookingsPerMonth')}</th>
          <SortHeader<UserSortKey>
            label={t('users.status')}
            sortKey="status"
            active={sortKey}
            direction={sortDirection}
            onSort={onSort}
          />
          <th className="px-6 py-3 text-right">{t('common.actions')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {users.map((user) => (
          <tr key={user.id} className="hover:bg-muted/30 transition-colors">
            {isSelectingMembers && (
              <td className="pl-6 pr-1 py-4">
                <input
                  type="checkbox"
                  checked={selectedMemberIds.has(user.id)}
                  onChange={handleToggleMember(user.id)}
                  aria-label={formatFullName(user.firstname, user.lastname)}
                  className="h-4 w-4 rounded border-border accent-violet-600"
                />
              </td>
            )}
            <td className="px-6 py-4 font-medium text-foreground">
              {formatFullName(user.firstname, user.lastname)}
            </td>
            <td className="px-6 py-4 text-muted-foreground">{user.email || '-'}</td>
            <td className="px-6 py-4 text-muted-foreground">{user.mobileNumber || '-'}</td>
            <td className="px-6 py-4">
              <RoleBadge role={user.role} />
            </td>
            <td className="px-6 py-4">
              <DriverGroupBadges groups={groupsByUserId.get(user.id) ?? []} />
            </td>
            <td className="px-6 py-4">
              <BookingUsage used={user.usedBookingsThisMonth} max={user.maxBookingsPerMonth} />
            </td>
            <td className="px-6 py-4">
              <UserStatusBadge status={user.status} />
            </td>
            <td className="px-6 py-4">
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleEditUser(user)}
                  title={t('common.edit')}
                >
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={handleDeleteUser(user)}
                  title={t('common.delete')}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
